import { insightsIntl } from "../../strings/intl";

import { MetricTypes } from "../../autogen/metrics";
import { LtvTimeKeys } from "../../autogen/ltv-data";

export const LTV_CAC_SUMMARY = `
#### What is Customer Lifetime Value?
Customer lifetime value (LTV) is the total revenue generated from a customer over the whole period of their relationship with your business.

#### Why is Customer Lifetime Value important?
One goal of this analysis is to help you to determine how much you can afford to spend to acquire new customers.
Even if your customer acquisition cost is greater than your average order value, your acquisition efforts may become profitable at a later date after acquired customers make subsequent purchases.

#### What is this analysis and why is it valuable?
This report groups your customers into ‘cohorts’ based on when they placed their first order with your store.
For each cohort, we calculate how much revenue, on average, you generate from a customer from their first purchase.
The average revenue continues to update as repeat customers make additional purchases over time.
Ultimately, this analysis allows you to estimate the lifetime value of your past and present customers.
`;

export const CHURN_SUMMARY = `
##### What is on this tab?
- Customer churn is the rate at which customers opt-out of purchasing more products.
- Broadly, this is the percentage of customers who don't go on to become repeat customers.

##### Why should I use this section?
Use this tab to understand two main insights:
1. What percentage of your new/existing customers are leaving, so you can decide if that's sustainable.
2. When more customers stick around and when they don't.

##### How should I use this section?
This tab has a configurable churn period.
- A churn period of three months makes sense for fast moving goods, whereas one of 12 months might make more sense for larger valued goods.
- This is very sensitive to company vertical, what is a high churn percentage for one industry may be very low for another.

##### Interpretation
For example, this tab is telling you:
- `;

// Any data past this point is rubbish
export const MAX_MONTHS_IN_PAST = 24;
export const MAX_QUARTERS_IN_PAST = 8;

export enum TempRes {
  Month = "Month",
  Quarter = "Quarter",
}

type DateRangeObj = { text: string; id: TempRes };

export const TemporalResOptions: DateRangeObj[] = [
  { text: insightsIntl.formatMessage({ id: "Month", defaultMessage: "Month" }), id: TempRes.Month },
  { text: insightsIntl.formatMessage({ id: "Quarter", defaultMessage: "Quarter" }), id: TempRes.Quarter },
];

export const LTV_METRICS = [
  MetricTypes.ALLTIME_AOV,
  MetricTypes.ALLTIME_CAC,
  MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT,
];
LtvTimeKeys.forEach((key) => {
  const metric = key.toUpperCase().replace(/[- ]/g, "_");
  LTV_METRICS.push(...[MetricTypes[`${metric}_LTV`]]);
});

const LTV_CAC_METRICS = [
  MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT,
  MetricTypes.ALLTIME_CUSTOMER_CURRENCY_CPC_SPEND,
  MetricTypes.ALLTIME_CAC,
];
["Alltime", "One Month", "Three Month", "Six Month", "Twelve Month"].forEach((key) => {
  const metric = key.toUpperCase().replace(/[- ]/g, "_");
  LTV_CAC_METRICS.push(...[MetricTypes[`${metric}_ORDERS`]]);
  LTV_CAC_METRICS.push(...[MetricTypes[`${metric}_NET_REVENUE`]]);
  LTV_CAC_METRICS.push(...[MetricTypes[`${metric}_AOV`]]);
  LTV_CAC_METRICS.push(...[MetricTypes[`${metric}_FREQUENCY`]]);
  LTV_CAC_METRICS.push(...[MetricTypes[`${metric}_LTV`]]);
});

export const LTV_NARROW_CURRENCIES = ["USD", "CAD"];
